<template>
  <div class="">
    <div class="tw:flexrow-0 tw:justify-stretch tw:ml-6">
      <!-- VERTICAL DIVIDER -->
      <div
        class="tw:w-3 tw:shrink-0 tw:pt-3"
        :class="{
          'tw:pb-4': showMoreVisible && !!iterableSubpageItems.length,
          'tw:pb-10': !showMoreVisible && !!iterableSubpageItems.length,
          'tw:pb-3': !iterableSubpageItems.length,
        }"
      >
        <div class="tw:w-[1px] tw:h-full tw:bg-gray-200"></div>
      </div>

      <!-- MAIN CONTENT -->
      <div class="tw:min-w-0 tw:grow tw:pb-1.5 tw:pt-0.5">
        <!-- SEARCH INPUT -->
        <data-input @input="debouncedSearch" color="grey darken-1" v-bind="searchTypeInput" class="tw:solo-input" />

        <!-- ELEMENTS LIST -->
        <v-list style="text-align: left !important" class="tw:px-0" dense>
          <v-list-item-group
            v-if="isLoading || !!iterableSubpageItems.length"
            color="primary"
            :value-comparator="activeElementComparator"
          >
            <!-- SKELETON LOADERS -->
            <template v-if="isLoading">
              <v-list-item v-for="(_, idx) in new Array(3).fill(null)" :key="idx">
                <v-list-item-content>
                  <v-list-item-title>
                    <div class="tw:flexrow-2">
                      <v-skeleton-loader
                        type="avatar"
                        height="12"
                        width="12"
                        max-width="12"
                        min-width="12"
                        class="tw:[&_.v-skeleton-loader\_\_avatar]:size-[12px] tw:w-auto"
                      />
                      <v-skeleton-loader type="text" class="tw:w-full" />
                    </div>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <!-- PARSED ITEMS -->
            <span
              v-else
              v-for="(_, idx) in new Array(
                iterableSubpageItems.length < currentMaxItems ? iterableSubpageItems.length : currentMaxItems
              ).fill(null)"
              :key="`${iterableSubpageItems[idx].id}-${idx}`"
              v-on="
                !!itemsTooltip
                  ? {
                      mouseenter: $event => (tooltipTarget = $event.target),
                      mouseleave: () => (tooltipTarget = null),
                    }
                  : undefined
              "
            >
              <v-list-item
                :value="iterableSubpageItems[idx].id"
                :input-value="isElementActive(idx)"
                class="tw:mb-1 tw:[.v-list-item--disabled]:opacity-50!"
                :disabled="itemsDisabled"
                @click="!isElementActive(idx) ? (() => $emit('change', iterableSubpageItems[idx].id))() : undefined"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <v-row no-gutters align="center" class="flex-nowrap tw:gap-2">
                      <v-col cols="auto">
                        <v-icon
                          size="18"
                          :color="
                            roleIcons[iterableSubpageItems[idx].role].iconColor ??
                            (isElementActive(idx) ? 'primary' : 'var(--tw-color-gray-800)')
                          "
                        >
                          {{ roleIcons[iterableSubpageItems[idx].role].icon }}
                        </v-icon>
                      </v-col>
                      <v-col
                        cols="auto"
                        :class="{ 'tw:text-gray-800': !isElementActive(idx) }"
                        class="tw:shrink tw:truncate tw:text-xs"
                      >
                        {{ iterableSubpageItems[idx].name }}
                      </v-col>
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </span>
          </v-list-item-group>
          <div v-else class="tw:text-xs tw:text-gray-500 tw:mt-3">
            {{ $i18n.t('default.noResults') }}
          </div>
        </v-list>

        <!-- SHOW MORE BUTTON -->
        <base-button v-if="showMoreVisible" type="text" color="primary" @click="showMore">
          {{ $i18n.t('sidebar.showMore') }}
        </base-button>
      </div>

      <!-- TOOLTIP -->
      <v-tooltip
        v-if="tooltipTarget"
        allow-overflow
        :activator="tooltipTarget"
        :value="!!tooltipTarget"
        color="font"
        bottom
      >
        {{ itemsTooltip }}
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import DataInput from '@/components/DataInput';

import { get } from 'vuex-pathify';
import { debounce } from 'lodash';

export default {
  name: 'SiderSubpageElements',
  components: {
    DataInput,
  },
  props: {
    activeValue: {
      type: [String, Number],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: null,
    },
    itemsDisabled: {
      type: Boolean,
      default: false,
    },
    itemsTooltip: {
      type: String,
    },
  },
  data: vm => ({
    iterableSubpageItems: vm.items,
    currentMaxItems: 10,
    tooltipTarget: null,
  }),
  computed: {
    currentUserId: get('users/currentUser@id'),
    showMoreVisible() {
      return !this.isLoading && this.currentMaxItems < this.iterableSubpageItems.length;
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        disabled: this.isLoading,
        hideDetails: true,
        clearable: true,
      };
    },
    roleIcons() {
      return {
        default: {
          icon: 'mdi-home',
        },
        predefined: {
          icon: 'mdi-star',
          iconColor: 'var(--tw-color-yellow-600)',
        },
        owner: {
          icon: 'mdi-account',
        },
        shared: {
          icon: 'mdi-account-group',
        },
      };
    },
  },
  watch: {
    items: {
      handler() {
        this.search();
      },
      immediate: true,
    },
  },
  methods: {
    activeElementComparator(_, value) {
      return value === this.activeValue;
    },
    isElementActive(idx) {
      return this.activeValue === this.iterableSubpageItems[idx]?.id;
    },
    search(text) {
      if (!text) this.iterableSubpageItems = [...this.items];
      else
        this.iterableSubpageItems = [...this.items].filter(item =>
          item.name?.toLowerCase().includes(text.toLowerCase())
        );
    },
    showMore() {
      const moreItems = this.currentMaxItems + 7;
      if (moreItems >= this.iterableSubpageItems.length) this.currentMaxItems = this.iterableSubpageItems.length;
      else this.currentMaxItems = moreItems;
    },
  },
  created() {
    this.debouncedSearch = debounce(this.search, 200);
  },
};
</script>
